import { useCallback, useState } from "react";

import type { User, UserClient, UserCluster } from "../../modules/users/types";
import type { Usina, UsinaByClient } from "../../modules/usinas/types";
import type { CenterScreenValue } from "../../modules/operations/centerScreensEnum";

type UseClientClusterUsinaFilterProps = {
    user: User | null;
    usinas: Usina[];
    selectedScreen: CenterScreenValue;
    setSelectedScreen: (screen: CenterScreenValue) => void;
}

type UseClientClusterUsinaFilterReturn = {
    selectedClientFilter: { id: string; name: string } | null;
    setSelectedClientFilter: (client: { id: string; name: string } | null | undefined) => void;

    selectedCluster: { id: string; name: string } | null;
    setSelectedCluster: (cluster: { id: string; name: string } | null | undefined) => void;

    selectedUsinaFilter: { id: string; name: string } | null;
    setSelectedUsinaFilter: (usina: { id: string; name: string } | null | undefined) => void;

    availableClients: UserClient[];
    availableClusters: UserCluster[];
    availableUsinas: UsinaByClient[];

    selectedScreen: CenterScreenValue;
    setSelectedScreen: (screen: CenterScreenValue) => void;
}

type FilterState = {
    selectedClientFilter: { id: string; name: string } | null;
    selectedCluster: { id: string; name: string } | null;
    selectedUsinaFilter: { id: string; name: string } | null;
}

function clientHasUsinaInCluster(userClient: UserClient, clusterId: string, usinas: Usina[]) {
    return userClient.usinas.some(usinaId => clusterHasUsina(clusterId, usinaId, usinas));
}

function clientHasUsina(userClient: UserClient, usinaId: string) {
    return userClient.usinas.includes(usinaId);
}

function usinaBelongsToAnyUserClient(usinaId: string, userClients: UserClient[]) {
    return userClients.some((client) => clientHasUsina(client, usinaId));
}

function clusterHasClientUsina(clusterId: string, clientId: string, userClients: UserClient[], usinas: Usina[]) {
    const client = userClients.find(c => c.id === clientId);
    return !!client && clientHasUsinaInCluster(client, clusterId, usinas);
}

function clusterHasUsina(clusterId: string, usinaId: string, usinas: Usina[]) {
    const usina = usinas.find(u => u.usinaIds.id === usinaId);
    const usinaClusterId = usina?.clusterId;
    return !!usinaClusterId && usinaClusterId === clusterId;
}

function usinaHasClient(usina: Usina, clientId: string, userClients: UserClient[]) {
    const client = userClients.find(c => c.id === clientId);
    return !!client && clientHasUsina(client, usina.usinaIds.id);
}

function usinaHasCluster(usina: Usina, clusterId: string, usinas: Usina[]) {
    return clusterHasUsina(clusterId, usina.usinaIds.id, usinas);
}

function getAvailableClients(filterState: FilterState, user: User | null, usinas: Usina[]) {
    const userClients = user?.clients || [];

    return userClients.filter(client => {
        let hasClusterMatch = true;
        if (filterState.selectedCluster) {
            hasClusterMatch = clientHasUsinaInCluster(client, filterState.selectedCluster.id, usinas);
        }

        let hasUsinaMatch = true;
        if (filterState.selectedUsinaFilter) {
            hasUsinaMatch = clientHasUsina(client, filterState.selectedUsinaFilter.id);
        }

        return hasClusterMatch && hasUsinaMatch;
    });
}

function getAvailableClusters(filterState: FilterState, user: User | null, usinas: Usina[]) {
    const userClients = user?.clients || [];
    const userClusters = user?.clusterIdAndName || [];


    return userClusters.filter(cluster => {

        let hasClientMatch = true;
        if (filterState.selectedClientFilter) {
            hasClientMatch = clusterHasClientUsina(cluster.id, filterState.selectedClientFilter.id, userClients, usinas);
        }

        let hasUsinaMatch = true;
        if (filterState.selectedUsinaFilter) {
            hasUsinaMatch = clusterHasUsina(cluster.id, filterState.selectedUsinaFilter.id, usinas);
        }

        return hasClientMatch && hasUsinaMatch;
    });
}

function getAvailableUsinas(
    filterState: FilterState,
    user: User | null,
    usinas: Usina[],
): UsinaByClient[] {
    const userClients = user?.clients || [];

    const acceptedUsinas = usinas.filter(usina => {

        let hasClientMatch = true;
        if (filterState.selectedClientFilter) {
            hasClientMatch = usinaHasClient(usina, filterState.selectedClientFilter.id, userClients);
        } else if (!filterState.selectedCluster && userClients.length > 0) {
            hasClientMatch = usinaBelongsToAnyUserClient(usina.usinaIds.id, userClients);
        }

        let hasClusterMatch = true;
        if (filterState.selectedCluster) {
            hasClusterMatch = usinaHasCluster(usina, filterState.selectedCluster.id, usinas);
        }

        return hasClientMatch && hasClusterMatch;
    });

    return acceptedUsinas.map(usina => ({
        usinaIds: usina.usinaIds,
        name: usina.name ?? "",
        clusterId: usina.clusterId || null,
        cluster: { clusterId: usina.clusterId, clusterName: usina.clusterName ?? "" }
    })) as UsinaByClient[];
}

export function useClientClusterUsinaFilter({ user, usinas, selectedScreen, setSelectedScreen }: UseClientClusterUsinaFilterProps): UseClientClusterUsinaFilterReturn {

    const usinasList = Array.isArray(usinas) ? usinas : [];

    const [filterState, setFilterState] = useState<FilterState>({
        selectedClientFilter: null,
        selectedCluster: null,
        selectedUsinaFilter: null,
    });

    const availableClients = getAvailableClients(filterState, user, usinasList);
    const availableClusters = getAvailableClusters(filterState, user, usinasList);
    const availableUsinas = getAvailableUsinas(filterState, user, usinasList);

    const setSelectedClientFilter = useCallback((client: { id: string; name: string } | null | undefined) => {
        setFilterState((prevState) => {
            return {
                ...prevState,
                selectedClientFilter: client?.id ? client : null,
            }
        });
    }, [user, usinasList]);

    const setSelectedCluster = useCallback((cluster: { id: string; name: string } | null | undefined) => {
        setFilterState((prevState) => ({
            ...prevState,
            selectedCluster: cluster?.id ? cluster : null,
        }));
    }, [user, usinasList]);

    const setSelectedUsinaFilter = useCallback((usina: { id: string; name: string } | null | undefined) => {
        setFilterState((prevState) => ({
            ...prevState,
            selectedUsinaFilter: usina?.id ? usina : null,
        }));
    }, [user, usinasList]);

    return {
        availableClients,
        availableClusters,
        availableUsinas,

        selectedClientFilter: filterState.selectedClientFilter,
        setSelectedClientFilter,

        selectedCluster: filterState.selectedCluster,
        setSelectedCluster,

        selectedUsinaFilter: filterState.selectedUsinaFilter,
        setSelectedUsinaFilter,

        selectedScreen,
        setSelectedScreen
    }
}
